import React from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import type { IModalDialogContent, ModalDialogOptions, ModalWindow } from '@components/Modal.tsx';
import { ModalButtonType } from '@components/Modal.tsx';

export type CustomLinkResult = { customName: string | null };

type Props = { currentCustomName: string | null };

// Keep in sync with FileService.RESERVED_CUSTOMNAMES
const RESERVED_CUSTOMNAMES = new Set([
  'api', 'file', 'f', 'download', 'share', 'share-space', 'preview',
  'users', 'trash', 'sw', 'assets', 'upload',
]);

const CUSTOMNAME_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

function validateCustomName(customName: string): string | null {
  if (customName.length === 0) return null; // empty is treated as "clear"
  if (customName.length > 64) return 'Name is too long (max 64 characters).';
  if (!/^[a-z0-9-]+$/.test(customName)) return 'Use lowercase letters, digits and dashes only.';
  if (!CUSTOMNAME_PATTERN.test(customName)) return 'No leading, trailing or repeated dashes allowed.';
  if (RESERVED_CUSTOMNAMES.has(customName)) return 'This name is reserved.';
  return null;
}

@observer
export class CustomLinkDialog extends React.Component<Props> implements IModalDialogContent<CustomLinkResult> {
  @observable private _value: string;
  @observable private _currentCustomName: string | null;

  constructor(props: Props) {
    super(props);
    makeObservable(this);
    this._currentCustomName = props.currentCustomName;
    this._value = this._currentCustomName ?? '';
  }

  private get _error(): string | null {
    return validateCustomName(this._value);
  }

  private get _resultUrl(): string | null {
    if (!this._value || this._error) return null;
    return `${window.location.origin}/f/${this._value}`;
  }

  getModalOptions(window: ModalWindow<CustomLinkResult>): ModalDialogOptions<CustomLinkResult> {
    const canSave = !this._error && this._value !== (this._currentCustomName ?? '');

    const buttons: ModalDialogOptions<CustomLinkResult>['buttons'] = [
      {
        title: 'Save',
        type: ModalButtonType.Add,
        color: 'primary',
        isDisabled: !canSave,
        onClick: () => {
          window.setPayload({ customName: this._value });
          window.close(ModalButtonType.Add);
        },
      },
    ];

    if (this._currentCustomName) {
      buttons.push({
        title: 'Clear Link',
        type: ModalButtonType.Remove,
        color: 'danger',
        onClick: () => {
          window.setPayload({ customName: null });
          window.close(ModalButtonType.Remove);
        },
      });
    }

    buttons.push({
      title: 'Cancel',
      type: ModalButtonType.Cancel,
      onClick: () => window.close(ModalButtonType.Cancel),
    });

    return {
      title: 'Custom Link',
      width: '420px',
      closeByEscape: true,
      closeOnClickOutside: true,
      buttons,
    };
  }

  render() {
    const error = this._error;
    const url = this._resultUrl;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
            Link name <span style={{ opacity: 0.6 }}>(lowercase, digits, dashes)</span>
          </label>
          <input
            type="text"
            className="form-control"
            value={this._value}
            placeholder="my-file-name"
            spellCheck={false}
            autoFocus
            onChange={this._onChange}
          />
          {error && <div style={{ color: 'var(--danger-color)', fontSize: '12px', marginTop: '6px' }}>{error}</div>}
        </div>
        {url && (
          <div style={{ fontSize: '13px' }}>
            <span style={{ opacity: 0.7 }}>Your link: </span>
            <span style={{ wordBreak: 'break-all' }}>{url}</span>
          </div>
        )}
      </div>
    );
  }

  @action.bound
  private _onChange(e: React.ChangeEvent<HTMLInputElement>) {
    this._value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
  }
}
